import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import '../styles/AdminDashboard.css';
import { getCurrentISTTime } from '../utils/timeUtils';

const AdminDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [logs, setLogs] = useState([]);
    const [logType, setLogType] = useState('app');
    const [logLines, setLogLines] = useState(100);
    const [logSearch, setLogSearch] = useState('');

    useEffect(() => {
        loadDashboard();
        const interval = setInterval(loadDashboard, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/admin/dashboard');
            if (response.data.success) {
                setDashboardData(response.data);
                setError(null);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error loading dashboard');
            console.error('Dashboard error:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadLogs = async () => {
        try {
            const response = await axiosInstance.get('/admin/logs', {
                params: {
                    type: logType,
                    lines: logLines,
                    search: logSearch
                }
            });
            if (response.data.success) {
                setLogs(response.data.logs);
            }
        } catch (err) {
            console.error('Error loading logs:', err);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (loading && !dashboardData) {
        return (
            <div className="admin-dashboard">
                <div className="loading">Loading admin dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-dashboard">
                <div className="error-message">{error}</div>
                <button onClick={loadDashboard} className="retry-btn">Retry</button>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <div className="header-content">
                    <h1>🖥️ StockKhata Admin Dashboard</h1>
                    <p>Real-time system monitoring and performance analytics</p>
                </div>
                <button onClick={logout} className="logout-btn">
                    🚪 Logout
                </button>
            </div>

            <div className="dashboard-grid">
                {/* System Status Card */}
                <div className="dashboard-card">
                    <h3>🖥️ System Status</h3>
                    {dashboardData?.system && (
                        <div className="metrics">
                            <div className="metric">
                                <span>CPU Usage</span>
                                <span>{dashboardData.system.cpu?.usage_percent || 0}%</span>
                            </div>
                            <div className="metric">
                                <span>Memory Usage</span>
                                <span>{dashboardData.system.memory?.usage_percent || 0}%</span>
                            </div>
                            <div className="metric">
                                <span>Memory Used</span>
                                <span>{dashboardData.system.memory?.used_gb || 0} GB</span>
                            </div>
                            <div className="metric">
                                <span>Disk Usage</span>
                                <span>{dashboardData.system.disk?.usage_percent || 0}%</span>
                            </div>
                            <div className="metric">
                                <span>Disk Free</span>
                                <span>{dashboardData.system.disk?.free_gb || 0} GB</span>
                            </div>
                            <div className="metric">
                                <span>Network Sent</span>
                                <span>{formatBytes(dashboardData.system.network?.bytes_sent || 0)}</span>
                            </div>
                            <div className="metric">
                                <span>Network Received</span>
                                <span>{formatBytes(dashboardData.system.network?.bytes_recv || 0)}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Performance Card */}
                <div className="dashboard-card">
                    <h3>⚡ Performance</h3>
                    {dashboardData?.performance && (
                        <div className="metrics">
                            <div className="metric">
                                <span>Avg Response Time</span>
                                <span>{dashboardData.performance.response_times?.average_ms || 0}ms</span>
                            </div>
                            <div className="metric">
                                <span>Min Response Time</span>
                                <span>{dashboardData.performance.response_times?.min_ms || 0}ms</span>
                            </div>
                            <div className="metric">
                                <span>Max Response Time</span>
                                <span>{dashboardData.performance.response_times?.max_ms || 0}ms</span>
                            </div>
                            <div className="metric">
                                <span>Total Requests</span>
                                <span>{dashboardData.performance.requests?.total || 0}</span>
                            </div>
                            <div className="metric">
                                <span>Error Rate</span>
                                <span>{dashboardData.performance.requests?.error_rate_percent || 0}%</span>
                            </div>
                            <div className="metric">
                                <span>Errors</span>
                                <span>{dashboardData.performance.requests?.errors || 0}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Database Status Card */}
                <div className="dashboard-card">
                    <h3>🗄️ Database</h3>
                    {dashboardData?.database && (
                        <div className="metrics">
                            <div className="metric">
                                <span>Status</span>
                                <span className={`status ${dashboardData.database.status === 'connected' ? 'online' : 'offline'}`}>
                                    {dashboardData.database.status || 'Unknown'}
                                </span>
                            </div>
                            <div className="metric">
                                <span>Collections</span>
                                <span>{dashboardData.database.collections || 0}</span>
                            </div>
                            <div className="metric">
                                <span>Total Documents</span>
                                <span>{dashboardData.database.total_documents || 0}</span>
                            </div>
                            {dashboardData.database.collection_stats && 
                                Object.entries(dashboardData.database.collection_stats).map(([name, stats]) => (
                                    <div key={name} className="metric">
                                        <span>{name}</span>
                                        <span>{stats.documents} docs ({stats.size_mb}MB)</span>
                                    </div>
                                ))
                            }
                        </div>
                    )}
                </div>

                {/* Security Events Card */}
                <div className="dashboard-card">
                    <h3>🛡️ Security</h3>
                    {dashboardData?.security && (
                        <div className="metrics">
                            <div className="metric">
                                <span>Total Events</span>
                                <span>{dashboardData.security.total_events || 0}</span>
                            </div>
                            <div className="metric">
                                <span>Rate Limit Violations</span>
                                <span>{dashboardData.security.event_counts?.rate_limit_violations || 0}</span>
                            </div>
                            <div className="metric">
                                <span>Invalid Tokens</span>
                                <span>{dashboardData.security.event_counts?.invalid_tokens || 0}</span>
                            </div>
                            <div className="metric">
                                <span>Auth Failures</span>
                                <span>{dashboardData.security.event_counts?.authentication_failures || 0}</span>
                            </div>
                            <div className="metric">
                                <span>Suspicious Activity</span>
                                <span>{dashboardData.security.event_counts?.suspicious_activity || 0}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Logs Section */}
                <div className="dashboard-card logs-section">
                    <h3>📋 System Logs</h3>
                    <div className="logs-controls">
                        <select value={logType} onChange={(e) => setLogType(e.target.value)}>
                            <option value="app">Application Logs</option>
                            <option value="error">Error Logs</option>
                            <option value="security">Security Logs</option>
                        </select>
                        <input
                            type="number"
                            value={logLines}
                            onChange={(e) => setLogLines(e.target.value)}
                            min="10"
                            max="1000"
                            placeholder="Lines"
                        />
                        <input
                            type="text"
                            value={logSearch}
                            onChange={(e) => setLogSearch(e.target.value)}
                            placeholder="Search logs..."
                        />
                        <button onClick={loadLogs} className="load-logs-btn">
                            Load Logs
                        </button>
                        <button onClick={loadDashboard} className="refresh-btn">
                            🔄 Refresh All
                        </button>
                    </div>
                    <div className="logs-container">
                        {logs.length > 0 ? (
                            logs.map((log, index) => (
                                <div key={index} className={`log-entry ${log.includes('ERROR') ? 'error' : log.includes('WARNING') ? 'warning' : 'info'}`}>
                                    {log}
                                </div>
                            ))
                        ) : (
                            <div className="no-logs">Select log type and click "Load Logs"</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="refresh-time">
                Last updated: {getCurrentISTTime()}
            </div>
        </div>
    );
};

export default AdminDashboard; 